import React, { useState, useEffect } from 'react';
import { Block } from '../classes/Block';
import { RowData } from '../App';
import './BlockConfigForm.css';
import { t } from '@lingui/core/macro';
import { Select, MenuItem } from '@mui/material';

interface LogoBlockConfigProps {
  block: Block;
  blockIndex: number;
  onBlockChange: (updatedBlock: Block) => void;
  onLogoRowSelect?: (logoRowIndex: number) => void;
}

interface LogoRowConfig {
  scale: number;
  xOffset: number;
  yOffset: number;
  autoCrop: boolean;
}

const LogoBlockConfig: React.FC<LogoBlockConfigProps> = ({ block, blockIndex, onBlockChange, onLogoRowSelect }) => {
  const [selectedRowIndex, setSelectedRowIndex] = useState<number>(0);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
  const [logoConfig, setLogoConfig] = useState<LogoRowConfig>({
    scale: 100,
    xOffset: 0,
    yOffset: 0,
    autoCrop: false,
  });
  const baseId: string = `logo-block-${blockIndex}`;
  const paneContentId: string = `${baseId}-content`;

  // Load configuration from selected row
  useEffect(() => {
    if (!block.dataRows || block.dataRows.length === 0) {
      return;
    }

    const selectedRow: RowData = block.dataRows[selectedRowIndex] || block.dataRows[0];
    const col2Value: string | number = selectedRow.col2 as string | number;

    try {
      if (col2Value && typeof col2Value === 'string') {
        const parsed: LogoRowConfig = JSON.parse(col2Value);
        setLogoConfig({
          scale: parsed.scale || 100,
          xOffset: parsed.xOffset || 0,
          yOffset: parsed.yOffset || 0,
          autoCrop: parsed.autoCrop || false,
        });
      } else {
        setLogoConfig({ scale: 100, xOffset: 0, yOffset: 0, autoCrop: false });
      }
    } catch (e) {
      setLogoConfig({ scale: 100, xOffset: 0, yOffset: 0, autoCrop: false });
    }
  }, [selectedRowIndex, block.dataRows]);

  const handleConfigChange = (field: keyof LogoRowConfig, value: number | boolean) => {
    const newConfig: LogoRowConfig = { ...logoConfig, [field]: value };
    setLogoConfig(newConfig);

    // Update the row's col2 data
    const updatedDataRows: RowData[] = [...(block.dataRows || [])];
    if (updatedDataRows[selectedRowIndex]) {
      updatedDataRows[selectedRowIndex] = {
        ...updatedDataRows[selectedRowIndex],
        col2: JSON.stringify(newConfig),
      };

      // Create a new Block with updated data
      const updatedBlock: Block = Block.create({
        ...block,
        dataRows: updatedDataRows
      });

      onBlockChange(updatedBlock);
    }
  };

  const handleBlockPropertyChange = (field: 'marginColumn' | 'distanceBetweenRows', value: number) => {
    // Create a new Block with updated property
    const updatedBlock: Block = Block.create({
      ...block,
      marginColumn: field === 'marginColumn' ? value : block.marginColumn,
      distanceBetweenRows: field === 'distanceBetweenRows' ? value : block.distanceBetweenRows,
      dataRows: block.dataRows
    });

    onBlockChange(updatedBlock);
  };

  const logoSelectId: string = `${baseId}-row-select`;
  const scaleFieldId: string = `${baseId}-scale-${selectedRowIndex}`;
  const xOffsetFieldId: string = `${baseId}-x-offset-${selectedRowIndex}`;
  const yOffsetFieldId: string = `${baseId}-y-offset-${selectedRowIndex}`;
  const logoDistanceFieldId: string = `${baseId}-logo-distance`;
  const rowDistanceFieldId: string = `${baseId}-row-distance`;

  if (!block.dataRows || block.dataRows.length === 0) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#718096' }}>
        {t`No logo rows available. Add data to configure logos.`}
      </div>
    );
  }

  return (
    <div className="block-config-form logo-block-config" style={{ marginTop: '20px' }}>
      <div className={`config-pane ${isCollapsed ? 'collapsed' : ''}`}>
        <h3
          className="pane-title"
          role="button"
          tabIndex={0}
          aria-expanded={!isCollapsed}
          aria-controls={paneContentId}
          onClick={() => setIsCollapsed(prev => !prev)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setIsCollapsed(prev => !prev);
            }
          }}
        >
          {t`Logo Configuration`}
        </h3>
        <div className="pane-content" id={paneContentId}>
          <div className="logo-config-sections">
            <div className="logo-config-section">
              <div className="logo-config-section-title">{t`Logo Options`}</div>
              <div className="logo-config-grid logo-options-grid">
                <div className="form-tile logo-select-tile">
                  <label htmlFor={logoSelectId}>{t`Select Logo`}</label>
                  <div className="input-wrapper">
                    <Select
                      id={logoSelectId}
                      className="dropdown-menu-select"
                      value={selectedRowIndex}
                      onChange={(e) => {
                        const nextLogoRowIndex: number = Number(e.target.value);
                        setSelectedRowIndex(nextLogoRowIndex);
                        onLogoRowSelect?.(nextLogoRowIndex);
                      }}
                    >
                      {block.dataRows.map((row: RowData, index: number) => {
                        const logoName: string = String(row.col1 || `Logo ${index + 1}`);
                        return (
                          <MenuItem key={index} value={index}>
                            {logoName}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </div>
                </div>

                <div className="form-tile compact">
                  <label htmlFor={scaleFieldId}>{t`Scale`}</label>
                  <div className="input-wrapper">
                    <input
                      id={scaleFieldId}
                      type="number"
                      min="1"
                      max="400"
                      step="1"
                      value={logoConfig.scale}
                      onChange={(e) => {
                        const parsedValue: number = Number.parseInt(e.target.value, 10);
                        handleConfigChange('scale', Number.isNaN(parsedValue) ? 100 : parsedValue);
                      }}
                    />
                    <span className="unit">%</span>
                  </div>
                </div>

                <div className="form-tile compact">
                  <label htmlFor={xOffsetFieldId}>{t`X Offset`}</label>
                  <div className="input-wrapper">
                    <input
                      id={xOffsetFieldId}
                      type="number"
                      value={logoConfig.xOffset}
                      onChange={(e) => {
                        const parsedValue: number = Number.parseInt(e.target.value, 10);
                        handleConfigChange('xOffset', Number.isNaN(parsedValue) ? 0 : parsedValue);
                      }}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>

                <div className="form-tile compact">
                  <label htmlFor={yOffsetFieldId}>{t`Y Offset`}</label>
                  <div className="input-wrapper">
                    <input
                      id={yOffsetFieldId}
                      type="number"
                      value={logoConfig.yOffset}
                      onChange={(e) => {
                        const parsedValue: number = Number.parseInt(e.target.value, 10);
                        handleConfigChange('yOffset', Number.isNaN(parsedValue) ? 0 : parsedValue);
                      }}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>

                {/*
                <div className="form-tile compact checkbox-tile">
                  <label htmlFor={`${baseId}-auto-crop-${selectedRowIndex}`}>{t`Auto Crop`}</label>
                  <div className="input-wrapper checkbox-wrapper">
                    <input
                      id={`${baseId}-auto-crop-${selectedRowIndex}`}
                      type="checkbox"
                      checked={logoConfig.autoCrop}
                      onChange={(e) => handleConfigChange('autoCrop', e.target.checked)}
                    />
                  </div>
                </div>
                */}
              </div>
            </div>

            <div className="logo-config-section block-config-section">
              <div className="logo-config-section-title">{t`Block Layout`}</div>
              <div className="logo-config-grid block-layout-grid">
                <div className="form-tile compact">
                  <label htmlFor={logoDistanceFieldId}>{t`Logo Distance`}</label>
                  <div className="input-wrapper">
                    <input
                      id={logoDistanceFieldId}
                      type="number"
                      min="0"
                      value={block.marginColumn ?? 45}
                      onChange={(e) => {
                        const parsedValue: number = Number.parseInt(e.target.value, 10);
                        handleBlockPropertyChange('marginColumn', Number.isNaN(parsedValue) ? 0 : parsedValue);
                      }}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>

                <div className="form-tile compact">
                  <label htmlFor={rowDistanceFieldId}>{t`Distance Between Rows`}</label>
                  <div className="input-wrapper">
                    <input
                      id={rowDistanceFieldId}
                      type="number"
                      min="0"
                      value={block.distanceBetweenRows}
                      onChange={(e) => {
                        const parsedValue: number = Number.parseInt(e.target.value, 10);
                        handleBlockPropertyChange('distanceBetweenRows', Number.isNaN(parsedValue) ? 0 : parsedValue);
                      }}
                    />
                    <span className="unit">px</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogoBlockConfig;
